import { useCallback, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { useGetEnrollmentHYCSFundSuccessData, useSubmitEnrollmentHYCSFundSuccessData } from '@egw/api'
import { Button, GenericError } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentHYCSLayout } from '@egw/layouts'
import { sendEvent } from '@egw/events'
import { WEALTH_ROUTES } from '@egw/constants'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'

const log = new Logger('Enrollment - HYCS - FundSuccess')

export const FundSuccessPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const { data, isLoading, error } = useGetEnrollmentHYCSFundSuccessData()

  // Go to Core dashboard
  const onSubmit = useCallback(() => {
    sendEvent(EDataLayerEventWealth.BANKHYCHYS_VIEWACCOUNT_INTENT)
    navigate(WEALTH_ROUTES.HOME.path)
  }, [])

  return (
    <EnrollmentHYCSLayout title={t('hycs.fund_success.page_title')}>
      <div className="egw-container-enroll-hycs h-full flex flex-col relative md:justify-center justify-start items-start">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error ? (
          <div className="w-full flex-1 flex flex-col justify-start lg:justify-center">
            <CheckCircleIcon className="text-textSuccess w-[40px] h-[40px] mb-md stroke-[1]" />
            <h2 className="typo-primary-2xl-light text-textSuccess mb-md">{t('hycs.fund_success.title')}</h2>
            <span className="typo-primary-base text-text-body mb-md">{t('hycs.fund_success.body_1')}</span>
            <span className="typo-primary-base text-text-body mb-md">{t('hycs.fund_success.body_2')}</span>
            <span className="typo-primary-base text-text-body mb-md">{t('hycs.fund_success.body_3')}</span>
            <div className="flex-1 md:hidden" />

            {/* AUTO GENERATED COMPONENTS */}
            <Button type="button" className="w-full md:w-min mt-xl" onClick={onSubmit} testId="primaryAction_button">
              {t('hycs.fund_success.continue')}
            </Button>
          </div>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentHYCSLayout>
  )
}
